import { AgentAttachmentType } from '@multiplayer-app/ai-agent-types'
import type { AgentAttachment } from '@multiplayer-app/ai-agent-types'
import { FileText, Form, Globe, Link2, Paperclip, ScanText } from 'lucide-react'
import type { ReactNode } from 'react'
import { IconButton } from '../ui/IconButton'
import { formatFileSize } from '@/utils/fileValidation'
import { useTranslations } from '@/hooks/useTranslations'
import { useAgentRuntime } from '@/context/AgentProvider'
import type { FileUploadProgress } from '@/utils/fileUpload'

export interface AttachmentProps {
  attachments: AgentAttachment[]
  onRemove?: (id: string) => void
  uploadProgress?: Map<string, FileUploadProgress>
  uploadErrors?: Map<string, string>
}

const iconFor = (attachment: AgentAttachment, customIcons?: Record<string, ReactNode>): ReactNode => {
  switch (attachment.type) {
    case AgentAttachmentType.Link:
      return <Link2 size={12} />
    case AgentAttachmentType.Artifact:
      return <Paperclip size={12} />
    case AgentAttachmentType.Context: {
      const kind: string | undefined = (attachment as any)?.metadata?.kind
      if (kind && customIcons?.[kind]) return customIcons[kind]
      switch (kind) {
        case 'pageContext':
          return <Globe size={12} />
        case 'formSnapshot':
        case 'formField':
          return <Form size={12} />
        default:
          return <ScanText size={12} />
      }
    }
    case AgentAttachmentType.File:
    default:
      return <FileText size={12} />
  }
}

export const MessageAttachments = ({ attachments }: AttachmentProps) => {
  const { config } = useAgentRuntime()
  if (!attachments || attachments.length === 0) return null

  return (
    <div className='mp-agent-message-attachments' aria-label='Message attachments'>
      {attachments.map((att, index) => {
        const content = (
          <>
            <span className='mp-agent-message-attachment-icon'>{iconFor(att, config.contextAttachmentIcons)}</span>
            <span className='mp-agent-message-attachment-name'>{att.name}</span>
          </>
        )
        return att.url ? (
          <a
            key={att.id || index}
            className='mp-agent-message-attachment'
            href={att.url}
            target='_blank'
            rel='noreferrer'
            title={att.url}
          >
            {content}
          </a>
        ) : (
          <span key={att.id || index} className='mp-agent-message-attachment' title={att.mimeType ?? att.name}>
            {content}
          </span>
        )
      })}
    </div>
  )
}

export const ComposerAttachments = ({ attachments, onRemove, uploadProgress, uploadErrors }: AttachmentProps) => {
  const t = useTranslations()
  const { config } = useAgentRuntime()
  if (!attachments || attachments.length === 0) return null

  return (
    <div className='mp-agent-composer-attachments' aria-label={t.attachments}>
      {attachments.map((att) => {
        const progress = uploadProgress?.get(att.id)
        const error = uploadErrors?.get(att.name)
        const fileSize = (att.metadata as any)?.size || (att as any).size
        const sizeDisplay = fileSize ? ` (${formatFileSize(fileSize)})` : ''

        return (
          <span
            key={att.id}
            className={`mp-agent-attachment-chip ${error ? 'mp-agent-attachment-chip-error' : ''} ${
              progress?.status === 'uploading' ? 'mp-agent-attachment-chip-uploading' : ''
            }`}
            title={error || att.name}
          >
            <span className='mp-agent-attachment-chip-icon'>{iconFor(att, config.contextAttachmentIcons)}</span>
            <span className='mp-agent-attachment-chip-name' title={att.name}>
              {att.name}
              {sizeDisplay}
            </span>
            {progress?.status === 'uploading' && (
              <span className='mp-agent-attachment-chip-progress'>{progress.progress}%</span>
            )}
            {error && (
              <span className='mp-agent-attachment-chip-error-text' title={error}>
                ⚠
              </span>
            )}
            {onRemove ? (
              <IconButton
                size='sm'
                icon='X'
                type='button'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onRemove(att.id)
                }}
                aria-label={`Remove attachment ${att.name}`}
              />
            ) : null}
          </span>
        )
      })}
    </div>
  )
}
